/**
 * Tiers: the steps of the network's delivery waterfall. Each line item sits in
 * a tier, and the network's ordered list of tiers decides which tiers are
 * enabled and in what priority they compete for traffic. A tier gets its
 * exclusive share of traffic first, then competes with lower tiers up to its
 * maximum share.
 */

import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { fetchAllPages } from '@/services/core/paging'
import { getContext, NetworksApi, type Network } from '@/services/networks/networksApi'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

/** `Tier.Type` in core: house-ad tiers bypass floor prices. */
export const TIER_TYPES = ['STANDARD', 'HOUSE_ADS'] as const
export type TierType = (typeof TIER_TYPES)[number]

export interface TierDetail {
  id: string
  name: string
  description: string
  type: TierType
  /** Percentage of traffic the tier sees before competing with lower tiers. */
  minimumTraffic: number
  /** Percentage of traffic after which the tier stops competing. */
  maximumTraffic: number
  objectState: string
  createTime: string
  updateTime: string
  /** Problems the server found on the last save; shown on the edit page. */
  validationWarnings: ValidationWarning[]
}

export interface TierInput {
  name: string
  description?: string
  type?: TierType
  minimumTraffic?: number
  maximumTraffic?: number
  objectState?: string
}

function mapTier(r: Record<string, unknown>): TierDetail {
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    description: (r.description as string) ?? '',
    type: (r.type as TierType) ?? 'STANDARD',
    minimumTraffic: (r.minimumTraffic as number) ?? 100,
    maximumTraffic: (r.maximumTraffic as number) ?? 100,
    objectState: (r.objectState as string) ?? '',
    createTime: (r.createTime as string) ?? '',
    updateTime: (r.updateTime as string) ?? '',
    validationWarnings: mapValidationWarnings(r.validationWarnings),
  }
}

export const TiersApi = {
  /** Every tier, walked page by page — the priority list needs all of them. */
  getAll: (): Promise<TierDetail[]> =>
    fetchAllPages(async (page, pageSize) => {
      const params = new URLSearchParams({
        context: getContext(),
        includeActive: 'true',
        includeHidden: 'false',
        includeInactive: 'true',
        orderBy: 'name',
        orderByDirection: 'ASCENDING',
        page: String(page),
        pageSize: String(pageSize),
      })
      const res = await apiFetch(`${API_BASE}/tiers?${params}`, { headers: JSON_HEADERS })
      if (!res.ok) await throwApiError(res, 'Failed to fetch tiers')
      const data = (await res.json()) as { totalCount?: number }
      return {
        results: toArray<Record<string, unknown>>(data).map(mapTier),
        totalCount: data.totalCount,
      }
    }),

  getById: async (id: string): Promise<TierDetail> => {
    const res = await apiFetch(`${API_BASE}/tiers/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch tier')
    return mapTier(await res.json())
  },

  create: async (id: string, input: TierInput): Promise<TierDetail> => {
    const res = await apiFetch(`${API_BASE}/tiers/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id, ...input }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create tier')
    return mapTier(await res.json())
  },

  update: async (id: string, input: TierInput): Promise<TierDetail> => {
    const res = await apiFetch(`${API_BASE}/tiers/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save tier')
    return mapTier(await res.json())
  },

  /**
   * Saves the network's enabled tiers, highest priority first. A tier's
   * "not in the network's tiers" warning is only worked out when the tier
   * itself is saved, so every tier switched on or off is saved again after.
   */
  setNetworkTiers: async (network: Network, enabledIds: string[]): Promise<Network> => {
    const updated = await NetworksApi.update(network.id, { tiers: enabledIds.map((id) => ({ id })) })
    const before = network.tiers.map((tier) => tier.id)
    const changed = [
      ...enabledIds.filter((id) => !before.includes(id)),
      ...before.filter((id) => !enabledIds.includes(id)),
    ]
    await Promise.all(
      changed.map(async (id) => {
        const res = await apiFetch(`${API_BASE}/tiers/${id}?context=${getContext()}`, {
          method: 'POST',
          headers: JSON_HEADERS,
          body: '{}',
        })
        if (!res.ok) await throwApiError(res, 'Failed to save tier')
      })
    )
    return updated
  },
}

/**
 * The tiers in waterfall order: the network's enabled tiers by priority, then
 * the disabled ones by name. `priority` is 1-based, and null when disabled.
 */
export function orderTiersByNetwork(
  tiers: TierDetail[],
  networkTierIds: string[]
): (TierDetail & { priority: number | null })[] {
  const enabled = networkTierIds
    .map((id) => tiers.find((t) => t.id === id))
    .filter((t): t is TierDetail => !!t)
    .map((t, i) => ({ ...t, priority: i + 1 }))
  const disabled = tiers
    .filter((t) => !networkTierIds.includes(t.id))
    .map((t) => ({ ...t, priority: null }))
  return [...enabled, ...disabled]
}
